import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
const CTA_URL = 'https://wa.me/17863763985';
type FaqItem = {
  question: string;
  answer: React.ReactNode;
};
const FAQS: FaqItem[] = [{
  question: '¿En cuanto tiempo veré resultados?',
  answer: <>
        <p className="mb-3" data-id="element-286">
          Cada perfil de crédito es distinto, por lo que los tiempos varían
          según tu situación particular. El trabajo se realiza por rondas, y la{' '}
          <strong data-id="element-287">primera ronda de disputas</strong> suele completarse en
          aproximadamente 4 a 5 semanas.
        </p>
        <p data-id="element-288">
          El tiempo total del proceso depende de cada caso individual. No
          garantizamos resultados específicos.
        </p>
      </>
}, {
  question: '¿Tienen opciones de financiamiento?',
  answer: <p data-id="element-289">
        Sí. Todos nuestros planes incluyen opciones de financiamiento al{' '}
        <strong data-id="element-290">0% de interés</strong>, con pagos cómodos para que puedas
        comenzar tu proceso sin complicaciones.
      </p>
}, {
  question: '¿Por qué es obligatorio MyScoreIQ?',
  answer: <>
        <p className="mb-3" data-id="element-291">
          MyScoreIQ es la plataforma que utilizamos para acceder a tu reporte de
          crédito actualizado y monitorear el avance de cada ronda en tiempo
          real.
        </p>
        <p data-id="element-292">
          Mantener la membresía activa es indispensable para ejecutar el
          proceso. Al finalizar, puedes cancelarla directamente con el
          proveedor.
        </p>
      </>
}, {
  question: '¿Como funciona la Garantia?',
  answer: <p data-id="element-293">
        Ofrecemos una <strong data-id="element-294">Garantía de Entrega de Servicio</strong>: nos
        comprometemos a ejecutar el trabajo y el acompañamiento descritos en tu
        plan. Para conocer los términos completos, consulta nuestra{' '}
        <a href="/refund" className="text-[#7B2FE0] underline" data-id="element-295">
          política de reembolso
        </a>
        .
      </p>
}, {
  question: '¿Garantizan subir el puntaje de credito?',
  answer: <p data-id="element-296">
        No. Ninguna compañía puede garantizar legalmente un aumento de puntaje o
        un resultado específico. Nuestro enfoque es trabajar tu perfil mediante
        una <strong data-id="element-297">estrategia estructurada y educación financiera</strong>, ya
        que los resultados dependen de las decisiones de los burós de crédito y
        acreedores.
      </p>
}, {
  question: '¿Se pueden remover bancarrotas y desalojos?',
  answer: <p data-id="element-298">
        Revisamos cada elemento de tu reporte para identificar información que
        pueda ser inexacta, incompleta o no verificable bajo la ley (FCRA). No
        podemos garantizar la eliminación de ningún elemento; el resultado de
        cada disputa depende de la respuesta de los burós y acreedores.
      </p>
}, {
  question: '¿El proceso es 100% legal?',
  answer: <p data-id="element-299">
        Sí. Todo nuestro trabajo se realiza conforme a las leyes federales de
        protección al consumidor, incluyendo el{' '}
        <strong data-id="element-300">Fair Credit Reporting Act (FCRA)</strong> y la{' '}
        <strong data-id="element-301">Credit Repair Organizations Act (CROA)</strong>. Operamos con
        total transparencia en cada etapa.
      </p>
}];
function FaqRow({
  item,
  index
}: {
  item: FaqItem;
  index: number;
}) {
  const [open, setOpen] = useState(index === 0);
  return <div className="bg-[#F5F5F5] rounded-2xl overflow-hidden" data-id="element-302">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="w-full flex items-center justify-between gap-4 text-left px-5 md:px-6 py-5" data-id="element-303">
        <span className="text-base md:text-lg font-bold text-[#0A0A0A]" style={{
        fontFamily: 'Manrope, Inter, sans-serif'
      }} data-id="element-304">
          {item.question}
        </span>
        <motion.span animate={{
        rotate: open ? 180 : 0
      }} className="flex-shrink-0" data-id="element-305">
          <ChevronDownIcon className="w-5 h-5 text-[#7B2FE0]" data-id="element-306" />
        </motion.span>
      </button>
      <AnimatePresence initial={false} data-id="element-307">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-308">
            <div className="px-5 md:px-6 pb-5 text-sm md:text-base text-gray-600 leading-relaxed" data-id="element-309">
              {item.answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function LandingFAQ() {
  return <section className="w-full bg-white py-14 md:py-20 px-5" data-id="element-310">
      <div className="max-w-3xl mx-auto" data-id="element-311">
        <div className="text-center mb-12" data-id="element-312">
          <span className="inline-block bg-[#7B2FE0] text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full" data-id="element-313">
            Preguntas Frecuentes
          </span>
        </div>

        <div className="space-y-4" data-id="element-314">
          {FAQS.map((item, i) => <FaqRow key={item.question} item={item} index={i} data-id="element-315" />)}
        </div>

        <div className="text-center mt-12" data-id="element-316">
          <a href={CTA_URL} className="inline-block w-full sm:w-auto" data-id="element-317">
            <motion.button type="button" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} className="w-full sm:w-auto px-12 py-4 bg-[#7B2FE0] text-white font-bold text-base uppercase tracking-wide rounded-full shadow-lg shadow-[#7B2FE0]/30 hover:bg-[#6a23c7] transition-colors" data-id="element-318">
              Consulta Gratis
            </motion.button>
          </a>
        </div>
      </div>
    </section>;
}